// Turning untrusted HTML/Markdown into something safe to render, and
// deriving the course table of contents from headings.

import DOMPurify from "dompurify";
import { marked } from "marked";

/** Sanitize course HTML. A learner's copy is edited by the agent, so treat it as untrusted. */
export function sanitizeCourseHtml(html: string): string {
  return DOMPurify.sanitize(html, {
    FORBID_TAGS: ["style", "form", "input", "button", "iframe", "object", "embed"],
    FORBID_ATTR: ["style"],
  });
}

marked.setOptions({ gfm: true, breaks: false });

/** Render chat/FAQ Markdown to sanitized HTML. */
export function renderMarkdown(md: string): string {
  const html = marked.parse(md, { async: false }) as string;
  return DOMPurify.sanitize(html, { FORBID_TAGS: ["style", "form", "input", "iframe"], FORBID_ATTR: ["style"] });
}

export interface TocItem {
  id: string;
  text: string;
  level: 2 | 3;
  children: TocItem[];
}

/** Build a two-level table of contents from the h2/h3 headings (with ids) inside `root`. */
export function buildToc(root: ParentNode): TocItem[] {
  const toc: TocItem[] = [];
  root.querySelectorAll<HTMLElement>("h2[id], h3[id]").forEach((el) => {
    const item: TocItem = {
      id: el.id,
      text: (el.textContent ?? "").replace(/\s+/g, " ").trim(),
      level: el.tagName === "H2" ? 2 : 3,
      children: [],
    };
    if (!item.text) return;
    if (item.level === 2 || toc.length === 0) toc.push(item);
    else toc[toc.length - 1].children.push(item);
  });
  return toc;
}
